import React, { useState, useMemo } from 'react';
import useGameStore from '../store/gameStore';
import useAudioStore from '../store/audioStore';
import { useTranslation } from '../i18n';
import { LeaseBuyoutModal } from '../components/game/LeaseBuyoutModal';
import { LeaseConfirmModal } from '../components/game/LeaseConfirmModal';
import { LeaseBreakModal } from '../components/game/LeaseBreakModal';
import { ShoppingCart, CreditCard } from 'lucide-react';

import { FleetSummaryTiles } from '../components/fleet/FleetSummaryTiles';
import { SellConfirmation } from '../components/fleet/SellConfirmation';
import { OwnedTab } from '../components/fleet/OwnedTab';
import { BuyTab } from '../components/fleet/BuyTab';
import { LeaseTab } from '../components/fleet/LeaseTab';
import { DEFAULT_FLEET_FILTERS, filterVehicles } from '../utils/fleetFilters';

export default function Fleet() {
  const [tab, setTab] = useState('owned');
  const [expandedVehicle, setExpandedVehicle] = useState(null);
  const [sellTarget, setSellTarget] = useState(null);
  const [buyoutTarget, setBuyoutTarget] = useState(null);
  const [breakLeaseTarget, setBreakLeaseTarget] = useState(null);
  const [leaseConfirmTarget, setLeaseConfirmTarget] = useState(null);
  const [leaseDuration, setLeaseDuration] = useState(36);
  const {
    vehicles, drivers, treasury, totalDays, monthlyRevenues, activeContracts, phase,
    buyVehicle, sellVehicle, maintainVehicle, maintainAllVehicles,
    assignDriverToVehicle, leaseVehicle, computeLeaseBuyout, fileInsuranceClaim,
    toggleVehicleInterim,
  } = useGameStore();
  const playSound = useAudioStore((s) => s.play);
  const { t } = useTranslation();
  const [, setClaimResult] = useState(null);

  const maxTier = Math.max(...vehicles.map(v => v.tier), 1);

  const activeInvestments = useGameStore((s) => s.activeInvestments || {});

  const canUnlock = (v) => {
    // 04/02/2026 — Phase F4 : nouvelle logique déclarative via `unlockChecks`.
    // Fallback sur l'ancien parsing de string pour les véhicules non migrés
    // (sécurité — ne devrait plus se produire après la refonte).
    if (v.unlockChecks) {
      const c = v.unlockChecks;
      if (c.minMonths && totalDays < c.minMonths * 30) return false;
      if (c.minRevenue && monthlyRevenues < c.minRevenue) return false;
      if (Array.isArray(c.requiresInvestments)) {
        for (const invId of c.requiresInvestments) {
          if (!activeInvestments[invId]) return false;
        }
      }
      return true;
    }
    if (v.unlockedIf === 'Départ') return true;
    if (v.unlockedIf.includes('Tréso') || v.unlockedIf.includes('Trésorerie')) return treasury > 8000;
    if (v.unlockedIf.includes('1 mois')) return totalDays >= 30;
    if (v.unlockedIf.includes('6 mois')) return totalDays >= 30; // softened
    if (v.unlockedIf.includes('1 an')) return totalDays >= 90;
    if (v.unlockedIf.includes('5 ans')) return totalDays >= 365;
    if (v.unlockedIf.includes('CA > 8')) return monthlyRevenues >= 8000;
    if (v.unlockedIf.includes('CA > 12')) return monthlyRevenues >= 12000;
    if (v.unlockedIf.includes('CA > 15')) return monthlyRevenues >= 15000;
    if (v.unlockedIf.includes('CA > 18')) return monthlyRevenues >= 18000;
    if (v.unlockedIf.includes('CA > 20')) return monthlyRevenues >= 20000;
    if (v.unlockedIf.includes('CA > 25')) return monthlyRevenues >= 25000;
    if (v.unlockedIf.includes('CA > 60')) return monthlyRevenues >= 60000;
    return v.tier <= maxTier + 1;
  };

  // 11/02/2026 (fix user prod bug 1) — Ancienne clause `|| d.status === 'idle'`
  // classait n'importe quel chauffeur "idle" comme dispo, même s'il avait un
  // `assignedVehicleId`. Nouvelle règle stricte : chauffeur dispo = status
  // 'idle' ET sans véhicule assigné. Retiré aussi car l'UI de picking a
  // été retirée (voir VehicleRow.jsx). On garde la variable pour d'autres
  // usages potentiels (KPIs) — usage propre et lisible.
  const idleDrivers = drivers.filter(d => d.status === 'idle' && !d.assignedVehicleId);

  const getSellPrice = (vehicle) => {
    const ageDays = totalDays - (vehicle.purchaseDay || 0);
    const ageMonths = ageDays / 30;
    const baseSellRate = 0.60;
    const wearPenalty = ((vehicle.wear || 0) / 100) * 0.15;
    const agePenalty = Math.min(ageMonths * 0.01, 0.20);
    const sellRate = Math.max(0.15, baseSellRate - wearPenalty - agePenalty);
    return Math.round(vehicle.price * sellRate);
  };

  const handleSell = (vehicle) => {
    if (vehicle.status === 'active') return;
    setSellTarget(vehicle);
  };

  const confirmSell = () => {
    if (sellTarget) {
      playSound('sell');
      sellVehicle(sellTarget.id);
      setSellTarget(null);
    }
  };

  const totalValue = vehicles.reduce((sum, v) => sum + getSellPrice(v), 0);
  const activeVehicles = vehicles.filter(v => v.status === 'active').length;
  const brokenVehicles = vehicles.filter(v => v.status === 'broken').length;
  const leasedVehicles = vehicles.filter(v => v.isLeased).length;
  const monthlyLeasingCost = vehicles.reduce((sum, v) => sum + (v.isLeased ? (v.leaseMonthlyPayment || 0) : 0), 0);

  // 18/09/2026 (REX apprenant) — filtres « Ma flotte » : catégorie /
  // utilisation / propriété / motorisation (cf. utils/fleetFilters.js).
  const [filters, setFilters] = useState(DEFAULT_FLEET_FILTERS);
  const filteredVehicles = useMemo(() => filterVehicles(vehicles, filters), [vehicles, filters]);

  const vehiclesByTier = useMemo(() => {
    const groups = {};
    filteredVehicles.forEach(v => {
      const tr = v.tier || 1;
      if (!groups[tr]) groups[tr] = [];
      groups[tr].push(v);
    });
    return Object.entries(groups).sort((a, b) => Number(a[0]) - Number(b[0]));
  }, [filteredVehicles]);

  return (
    <div className="space-y-4" data-testid="fleet-page">
      <h2 className="text-2xl font-bold text-foreground">{t('fleet_title')}</h2>

      <FleetSummaryTiles
        vehicles={vehicles}
        activeVehicles={activeVehicles}
        totalValue={totalValue}
        brokenVehicles={brokenVehicles}
      />

      <div className="flex gap-2 overflow-x-auto pb-1">
        <button
          onClick={() => setTab('owned')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
            tab === 'owned' ? 'bg-accent text-white' : 'bg-ink-800 text-slate-400 border border-ink-600'
          }`}
          data-testid="tab-owned"
        >
          {t('my_fleet')} ({vehicles.length})
        </button>
        <button
          onClick={() => setTab('buy')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
            tab === 'buy' ? 'bg-accent text-white' : 'bg-ink-800 text-slate-400 border border-ink-600'
          }`}
          data-testid="tab-buy"
        >
          <ShoppingCart className="w-4 h-4 inline mr-1" /> {t('buy_tab')}
        </button>
        <button
          onClick={() => setTab('lease')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
            tab === 'lease' ? 'bg-accent text-white' : 'bg-ink-800 text-slate-400 border border-ink-600'
          }`}
          data-testid="tab-lease"
        >
          <CreditCard className="w-4 h-4 inline mr-1" /> {t('leasing_tab')}
        </button>
      </div>

      {tab === 'owned' && (
        <OwnedTab
          vehicles={filteredVehicles}
          allVehicles={vehicles}
          vehiclesByTier={vehiclesByTier}
          filters={filters}
          setFilters={setFilters}
          drivers={drivers}
          treasury={treasury}
          totalDays={totalDays}
          activeContracts={activeContracts}
          idleDrivers={idleDrivers}
          maintainAllVehicles={maintainAllVehicles}
          playSound={playSound}
          expandedVehicle={expandedVehicle}
          setExpandedVehicle={setExpandedVehicle}
          getSellPrice={getSellPrice}
          handleSell={handleSell}
          maintainVehicle={maintainVehicle}
          assignDriverToVehicle={assignDriverToVehicle}
          computeLeaseBuyout={computeLeaseBuyout}
          setBuyoutTarget={setBuyoutTarget}
          setBreakLeaseTarget={setBreakLeaseTarget}
          fileInsuranceClaim={fileInsuranceClaim}
          setClaimResult={setClaimResult}
          toggleVehicleInterim={toggleVehicleInterim}
        />
      )}

      {tab === 'buy' && (
        <BuyTab
          vehicles={vehicles}
          treasury={treasury}
          canUnlock={canUnlock}
          buyVehicle={buyVehicle}
          playSound={playSound}
        />
      )}

      {tab === 'lease' && (
        <LeaseTab
          treasury={treasury}
          leasedVehicles={leasedVehicles}
          monthlyLeasingCost={monthlyLeasingCost}
          leaseDuration={leaseDuration}
          setLeaseDuration={setLeaseDuration}
          setLeaseConfirmTarget={setLeaseConfirmTarget}
          canUnlock={canUnlock}
          phase={phase}
          totalFleetSize={vehicles.length}
        />
      )}

      {sellTarget && (
        <SellConfirmation
          vehicle={sellTarget}
          sellPrice={getSellPrice(sellTarget)}
          onConfirm={confirmSell}
          onCancel={() => setSellTarget(null)}
          t={t}
        />
      )}

      {buyoutTarget && (
        <LeaseBuyoutModal
          vehicle={buyoutTarget}
          onClose={() => setBuyoutTarget(null)}
        />
      )}

      {breakLeaseTarget && (
        <LeaseBreakModal
          vehicle={breakLeaseTarget}
          onClose={() => setBreakLeaseTarget(null)}
        />
      )}

      {leaseConfirmTarget && (
        <LeaseConfirmModal
          vehicleData={leaseConfirmTarget}
          durationMonths={leaseDuration}
          treasury={treasury}
          onClose={() => setLeaseConfirmTarget(null)}
          onConfirm={() => {
            playSound('buy');
            leaseVehicle(leaseConfirmTarget.id, leaseDuration);
            setLeaseConfirmTarget(null);
          }}
        />
      )}
    </div>
  );
}
